import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
    Plus, Pulse, ArrowUpRight, ArrowsClockwise, CheckCircle, WarningCircle, Pause, Play,
    MagnifyingGlass, RedditLogo, YoutubeLogo, TiktokLogo, InstagramLogo, X,
    CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { StreamErrorStripWithModal } from "../components/StreamErrorViews";
import PartnerPdfButton from "../components/PartnerPdfButton";

const CADENCES = [
    { value: "manual", label: "Manual only" },
    { value: "hourly", label: "Hourly" },
    { value: "every_6h", label: "Every 6 hours" },
    { value: "every_12h", label: "Every 12 hours" },
    { value: "daily", label: "Daily" },
    { value: "every_2_days", label: "Every 2 days" },
    { value: "weekly", label: "Weekly (default — saves Apify credits)" },
    { value: "monthly", label: "Monthly" },
];

const PLATFORM_META = {
    Reddit: { Icon: RedditLogo, color: "#FF4500" },
    YouTube: { Icon: YoutubeLogo, color: "#FF0033" },
    TikTok: { Icon: TiktokLogo, color: "#0d0d0d" },
    Instagram: { Icon: InstagramLogo, color: "#C13584" },
};

const DISPLAY_STATE_TONES = {
    "Configured":           { cls: "bg-muted text-muted-foreground",      hint: "Not yet set up to listen — add topics and platforms." },
    "Awaiting First Ingest":{ cls: "bg-primary/10 text-primary",           hint: "Configured but hasn't completed a discovery pass yet. Click 'Search now' to begin collecting signals." },
    "Collecting":           { cls: "bg-primary text-primary-foreground",   hint: "Currently searching platforms for matching conversations." },
    "Active":               { cls: "bg-emerald-500/15 text-emerald-700",   hint: "Listening on schedule and collecting signals." },
    "Paused":               { cls: "bg-muted text-muted-foreground",       hint: "Scheduler is paused for this stream." },
    "Stalled":              { cls: "bg-accent/15 text-accent",              hint: "Last ingest failed — check the error below." },
};

function StateChip({ state }) {
    const tone = DISPLAY_STATE_TONES[state] || DISPLAY_STATE_TONES["Configured"];
    return (
        <span
            data-testid={`state-chip-${String(state).replace(/\s+/g, "-").toLowerCase()}`}
            title={tone.hint}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-mono text-[10px] tracking-[0.14em] uppercase ${tone.cls}`}
        >
            {state}
        </span>
    );
}

function StatusDot({ status }) {
    const map = {
        idle: { cls: "bg-muted-foreground/40", label: "idle" },
        ingesting: { cls: "bg-primary animate-pulse", label: "searching" },
        completed: { cls: "bg-emerald-500", label: "live" },
        failed: { cls: "bg-accent", label: "failed" },
    };
    const s = map[status] || map.idle;
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${s.cls}`} />
            <span className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground">{s.label}</span>
        </span>
    );
}

/** A simple multi-tag input — type and press Enter (or comma) to add. */
function TagInput({ value, onChange, placeholder, testid }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const t = draft.trim().replace(/,$/, "");
        if (!t) return;
        if (!value.includes(t)) onChange([...value, t]);
        setDraft("");
    };
    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    return (
        <div data-testid={testid} className="rounded-sm border border-input bg-background px-2 py-1.5 min-h-[42px] flex flex-wrap gap-1.5 items-center focus-within:ring-1 focus-within:ring-primary">
            {value.map((t, i) => (
                <span key={`${t}-${i}`} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-[12px] font-medium px-2 py-0.5 rounded-sm">
                    {t}
                    <button type="button" onClick={() => remove(i)} className="hover:text-accent" aria-label={`Remove ${t}`}>
                        <X size={11} weight="bold" />
                    </button>
                </span>
            ))}
            <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        add();
                    } else if (e.key === "Backspace" && !draft && value.length) {
                        onChange(value.slice(0, -1));
                    }
                }}
                onBlur={add}
                placeholder={placeholder}
                className="flex-1 min-w-[140px] bg-transparent text-sm outline-none py-0.5"
            />
        </div>
    );
}

function PlatformPicker({ selected, onChange, available }) {
    const toggle = (p) => {
        if (selected.includes(p)) onChange(selected.filter((x) => x !== p));
        else onChange([...selected, p]);
    };
    return (
        <div data-testid="platform-picker" className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {available.map((p) => {
                const meta = PLATFORM_META[p] || {};
                const Icon = meta.Icon;
                const active = selected.includes(p);
                return (
                    <button
                        key={p}
                        type="button"
                        onClick={() => toggle(p)}
                        data-testid={`platform-${p}`}
                        className={[
                            "flex items-center gap-2 p-3 border rounded-sm text-sm transition-colors hover-lift",
                            active
                                ? "border-primary bg-primary/5 text-foreground"
                                : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/50",
                        ].join(" ")}
                    >
                        {Icon && <Icon size={18} weight={active ? "fill" : "duotone"} className={active ? "text-primary" : ""} />}
                        <span className="font-medium">{p}</span>
                        {active && <CheckCircle size={13} weight="fill" className="ml-auto text-primary" />}
                    </button>
                );
            })}
        </div>
    );
}

function StreamCard({ stream, onIngest, onRefresh }) {
    const platforms = stream.platforms?.length ? stream.platforms : (stream.platform_filters || []);
    const topics = stream.topics || [];
    const subreddits = stream.subreddits || [];

    return (
        <Link
            to={`/streams/${stream.id}`}
            data-testid={`stream-card-${stream.id}`}
            className="block border border-border bg-card p-6 hover-lift hover:border-primary group"
        >
            <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <StateChip state={stream.display_state || "Configured"} />
                        <span className="overline text-[9px]">
                            {CADENCES.find((c) => c.value === stream.cadence)?.label || stream.cadence}
                        </span>
                    </div>
                    <h2 className="font-display font-bold text-2xl tracking-tight leading-tight group-hover:text-primary transition-colors">
                        {stream.name}
                    </h2>
                    {stream.description && (
                        <p className="editorial text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2 max-w-md">
                            {stream.description}
                        </p>
                    )}
                    {(stream.display_state === "Awaiting First Ingest" || stream.display_state === "Configured") && (
                        <p className="mt-3 text-[11px] text-muted-foreground bg-muted/50 border-l-2 border-primary pl-3 py-1.5 rounded-sm max-w-md">
                            {stream.display_state === "Configured"
                                ? "This stream is configured but has no topics to search. Edit it to add discovery terms."
                                : "This stream is configured but has not yet completed a discovery pass. Click 'Search now' to begin collecting signals."}
                        </p>
                    )}
                </div>
                <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary shrink-0 mt-1" />
            </div>

            {topics.length > 0 && (
                <div className="mb-4">
                    <div className="overline text-[9px] mb-2">Listening for</div>
                    <div className="flex flex-wrap gap-1.5">
                        {topics.slice(0, 5).map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded-sm font-medium">
                                <MagnifyingGlass size={9} weight="bold" />
                                {t}
                            </span>
                        ))}
                        {topics.length > 5 && <span className="text-[11px] text-muted-foreground">+{topics.length - 5}</span>}
                    </div>
                </div>
            )}

            {platforms.length > 0 && (
                <div className="mb-4 flex items-center gap-3">
                    <div className="overline text-[9px]">Across</div>
                    <div className="flex gap-2">
                        {platforms.map((p) => {
                            const Icon = PLATFORM_META[p]?.Icon;
                            return Icon ? (
                                <Icon key={p} size={14} weight="duotone" className="text-foreground/60" />
                            ) : (
                                <span key={p} className="text-[10px] font-mono">{p}</span>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div>
                    <div className="overline text-[9px] mb-1">Signals</div>
                    <div className="font-display font-semibold text-xl tabular-nums">{stream.signal_count || 0}</div>
                </div>
                <div>
                    <div className="overline text-[9px] mb-1">Velocity</div>
                    <div className="font-display font-semibold text-xl tabular-nums">{(stream.last_velocity || 0).toFixed(1)}</div>
                    <div className="text-[9px] text-muted-foreground font-mono">/day · 7d</div>
                </div>
                <div>
                    <div className="overline text-[9px] mb-1">Last seen</div>
                    <div className="text-sm font-mono">
                        {stream.last_ingested_at ? new Date(stream.last_ingested_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                    </div>
                </div>
            </div>

            {subreddits.length > 0 && (
                <div className="mt-3 flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
                    <span className="overline text-[9px]">Pinned</span>
                    {subreddits.slice(0, 3).map((s) => (<span key={s}>{s}</span>))}
                    {subreddits.length > 3 && <span>+{subreddits.length - 3}</span>}
                </div>
            )}

            {stream.last_error && (
                <StreamErrorStripWithModal
                    stream={stream}
                    onRetried={onRefresh}
                    testid={`stream-error-strip-${stream.id}`}
                />
            )}

            <div className="mt-4 flex justify-end gap-4">
                <PartnerPdfButton streamId={stream.id} streamName={stream.name} variant="compact" />
                <button
                    type="button"
                    onClick={async (e) => {
                        e.preventDefault(); e.stopPropagation();
                        try {
                            await api.patch(`/streams/${stream.id}`, { active: !stream.active });
                            toast.success(stream.active ? `${stream.name} paused — no more ingests until resumed` : `${stream.name} resumed`);
                            onRefresh?.();
                        } catch (err) {
                            toast.error(err?.response?.data?.detail || "Toggle failed");
                        }
                    }}
                    data-testid={`stream-toggle-${stream.id}`}
                    className={`text-xs font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1.5 border-b pb-0.5 ${stream.active ? "border-border hover:text-accent" : "border-emerald-600 text-emerald-700 hover:text-emerald-800"}`}
                    title={stream.active ? "Pause this stream — stops automatic ingests and saves Apify credits" : "Resume this stream — re-enables scheduled ingests"}
                >
                    {stream.active ? <Pause size={11} weight="fill" /> : <Play size={11} weight="fill" />}
                    {stream.active ? "Pause" : "Resume"}
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onIngest(stream.id); }}
                    data-testid={`stream-ingest-${stream.id}`}
                    disabled={stream.last_status === "ingesting" || !stream.active}
                    className="text-xs font-mono uppercase tracking-[0.18em] hover:text-primary inline-flex items-center gap-1.5 border-b border-border pb-0.5 disabled:opacity-40"
                    title={!stream.active ? "Resume this stream first to run a search" : ""}
                >
                    {stream.last_status === "ingesting" ? <ArrowsClockwise size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                    {stream.last_status === "ingesting" ? "Searching…" : "Search now"}
                </button>
            </div>
        </Link>
    );
}

function CreateStreamDialog({ onCreated, availablePlatforms }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [topics, setTopics] = useState([]);
    const [platforms, setPlatforms] = useState(availablePlatforms.length ? availablePlatforms.slice(0, 2) : []);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [keywords, setKeywords] = useState([]);
    const [hashtags, setHashtags] = useState([]);
    const [subreddits, setSubreddits] = useState([]);
    const [creators, setCreators] = useState([]);
    const [manualUrls, setManualUrls] = useState([]);
    const [emotionalThemes, setEmotionalThemes] = useState([]);
    const [adHooks, setAdHooks] = useState([]);
    const [brandMentions, setBrandMentions] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [topicTags, setTopicTags] = useState([]);
    const [cadence, setCadence] = useState("weekly");
    const [active, setActive] = useState(true);
    const [maxItems, setMaxItems] = useState(60);

    const reset = () => {
        setName(""); setDescription("");
        setTopics([]); setPlatforms(availablePlatforms.slice(0, 2));
        setKeywords([]); setHashtags([]); setSubreddits([]); setCreators([]);
        setManualUrls([]); setEmotionalThemes([]); setAdHooks([]);
        setBrandMentions([]); setQuestions([]); setTopicTags([]);
        setCadence("weekly"); setActive(true); setMaxItems(60);
        setShowAdvanced(false);
    };

    const submit = async () => {
        if (!name.trim()) {
            toast.error("Stream name is required");
            return;
        }
        const hasPrimary = topics.length > 0 || keywords.length > 0;
        const hasAdvanced = subreddits.length || hashtags.length || creators.length || manualUrls.length || adHooks.length || brandMentions.length || questions.length || emotionalThemes.length;
        if (!hasPrimary && !hasAdvanced) {
            toast.error("Add at least one topic, keyword, or advanced input.");
            return;
        }
        if (platforms.length === 0 && subreddits.length === 0 && manualUrls.length === 0) {
            toast.error("Choose at least one platform — or add subreddits / manual URLs.");
            return;
        }
        try {
            const r = await api.post("/streams", {
                name: name.trim(),
                description: description.trim(),
                topics, platforms,
                keywords, hashtags, subreddits, creators,
                manual_urls: manualUrls,
                emotional_themes: emotionalThemes,
                ad_hooks: adHooks,
                brand_mentions: brandMentions,
                questions,
                topic_tags: topicTags,
                tiktok_search_terms: [],
                cadence, active, max_items_per_run: Number(maxItems) || 60,
            });
            toast.success(`Stream "${r.data.name}" created`);
            reset();
            setOpen(false);
            onCreated?.(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create stream");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button data-testid="new-stream-btn" size="sm" className="rounded-sm">
                    <Plus size={14} weight="bold" className="mr-1.5" />
                    New stream
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-stream-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Create a narrative stream</DialogTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                        Define what to listen for. The system finds the conversations.
                    </p>
                </DialogHeader>

                <div className="space-y-5 mt-2">
                    {/* NAME */}
                    <div>
                        <Label className="text-xs">Stream name</Label>
                        <Input
                            data-testid="stream-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Dating Burnout"
                            className="rounded-sm"
                        />
                    </div>

                    <div>
                        <Label className="text-xs">Description (the thesis)</Label>
                        <Textarea
                            data-testid="stream-description-input"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="rounded-sm"
                            placeholder="What this stream is observing about modern dating culture."
                        />
                    </div>

                    {/* PRIMARY: TOPICS / KEYWORDS */}
                    <div>
                        <Label className="text-xs flex items-center gap-1.5 mb-1.5">
                            <MagnifyingGlass size={12} weight="bold" className="text-primary" />
                            Topics — what to search for <span className="text-accent">*</span>
                        </Label>
                        <TagInput
                            testid="stream-topics-input"
                            value={topics}
                            onChange={setTopics}
                            placeholder="dating burnout, swipe fatigue, ghosting…"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1.5">
                            Each becomes a search query across the platforms below.
                        </p>
                    </div>

                    {/* PRIMARY: PLATFORMS */}
                    <div>
                        <Label className="text-xs mb-2 block">Platforms to search <span className="text-accent">*</span></Label>
                        <PlatformPicker selected={platforms} onChange={setPlatforms} available={availablePlatforms} />
                    </div>

                    {/* CADENCE */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Cadence</Label>
                            <Select value={cadence} onValueChange={setCadence}>
                                <SelectTrigger data-testid="stream-cadence-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CADENCES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Max items per platform/topic</Label>
                            <Input data-testid="stream-max-items" type="number" value={maxItems} onChange={(e) => setMaxItems(e.target.value)} className="rounded-sm" />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 pb-1">
                        <Switch data-testid="stream-active-toggle" checked={active} onCheckedChange={setActive} />
                        <Label className="text-xs">Active (auto-search on cadence)</Label>
                    </div>

                    {/* ADVANCED */}
                    <div className="border-t border-border pt-4">
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            data-testid="toggle-advanced"
                            className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
                        >
                            {showAdvanced ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                            Advanced inputs <span className="opacity-50">— more ways to listen</span>
                        </button>
                        {showAdvanced && (
                            <div className="space-y-5 mt-4">
                                <InputBlock
                                    label="Hashtags"
                                    hint="TikTok / Instagram / YouTube hashtag-based discovery."
                                    placeholder="datingburnout, swipefatigue, intentionaldating"
                                    value={hashtags} onChange={setHashtags}
                                    testid="stream-hashtags-input"
                                />
                                <InputBlock
                                    label="Pinned subreddits"
                                    hint="Crawls these subreddits' hot pages in addition to topic search."
                                    placeholder="r/dating, r/datingoverthirty, r/hingeapp"
                                    value={subreddits} onChange={setSubreddits}
                                    testid="stream-subreddits-input"
                                />
                                <InputBlock
                                    label="Creator accounts"
                                    hint={`Format: "platform:handle" — e.g. tiktok:datingdoll, youtube:@channel.`}
                                    placeholder="tiktok:datingdoll, youtube:@channel, instagram:handle"
                                    value={creators} onChange={setCreators}
                                    testid="stream-creators-input"
                                />
                                <InputBlock
                                    label="Manual URLs"
                                    hint="Skip discovery — ingest comments directly from these URLs."
                                    placeholder="https://www.reddit.com/r/.../comments/..."
                                    value={manualUrls} onChange={setManualUrls}
                                    testid="stream-manual-urls-input"
                                />
                                <InputBlock
                                    label="Emotional themes"
                                    hint="Broader semantic prompts — used as extra search queries (semantic AI clustering coming next)."
                                    placeholder="dating feels transactional, intentional dating"
                                    value={emotionalThemes} onChange={setEmotionalThemes}
                                    testid="stream-emotional-themes-input"
                                />
                                <InputBlock
                                    label="Ad hooks"
                                    hint={"Test whether ad-line phrasing appears organically online — e.g. dating should not feel like a second job."}
                                    placeholder={"dating should not feel like a second job"}
                                    value={adHooks} onChange={setAdHooks}
                                    testid="stream-ad-hooks-input"
                                />
                                <InputBlock
                                    label="Brand mentions"
                                    hint="Monitor mentions of HAEVN or campaign-specific language."
                                    placeholder="haevn, haevn dating"
                                    value={brandMentions} onChange={setBrandMentions}
                                    testid="stream-brand-mentions-input"
                                />
                                <InputBlock
                                    label="Discovery questions"
                                    hint={`Find conversations where people ask or answer this. e.g. "Why are people tired of dating apps?".`}
                                    placeholder="why are people tired of dating apps?"
                                    value={questions} onChange={setQuestions}
                                    testid="stream-questions-input"
                                />
                                <InputBlock
                                    label="Keyword filter (post-search)"
                                    hint="After search results return, only keep comments containing one of these. Leave empty to keep everything."
                                    placeholder="exhausted, drained, tired"
                                    value={keywords} onChange={setKeywords}
                                    testid="stream-keywords-input"
                                />
                                <InputBlock
                                    label="Topic tags (applied to every signal)"
                                    placeholder="dating burnout, exhaustion"
                                    value={topicTags} onChange={setTopicTags}
                                    testid="stream-tags-input"
                                />
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="mt-2">
                    <Button data-testid="create-stream-confirm" onClick={submit} className="rounded-sm">
                        Create stream
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function InputBlock({ label, hint, placeholder, value, onChange, testid }) {
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <TagInput value={value} onChange={onChange} placeholder={placeholder} testid={testid} />
            {hint && <p className="text-[10px] text-muted-foreground mt-1.5">{hint}</p>}
        </div>
    );
}

export default function Streams() {
    const [streams, setStreams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchablePlatforms, setSearchablePlatforms] = useState(["Reddit", "YouTube", "TikTok", "Instagram"]);

    const load = async () => {
        try {
            const r = await api.get("/streams");
            setStreams(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/streams/searchable-platforms").then((r) => {
            if (r.data?.platforms?.length) setSearchablePlatforms(r.data.platforms);
        }).catch(() => {});
        load();
        const t = setInterval(load, 8000);
        return () => clearInterval(t);
    }, []);

    const ingest = async (id) => {
        try {
            await api.post(`/streams/${id}/ingest`);
            toast.success("Search started across platforms");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start search");
        }
    };

    return (
        <>
            <PageHeader
                overline="Topic-Driven Discovery"
                title="Narrative Streams"
                description="Each stream is a focused narrative discovery directive, not a wholesale crawl. The system runs targeted searches on Reddit, YouTube, TikTok and Instagram using your topic phrases, suggested communities, and related language — discovering adjacent conversations without restricting itself to predefined lists."
                testid="streams-header"
                actions={<CreateStreamDialog onCreated={load} availablePlatforms={searchablePlatforms} />}
            />

            <PageBody>
                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading streams…</div>
                ) : streams.length === 0 ? (
                    <EmptyState
                        title="No streams yet"
                        description="Try: 'Dating Burnout' searching Reddit and YouTube for topics like 'swipe fatigue' and 'tired of dating apps'. The system finds conversations across the platforms automatically."
                        testid="streams-empty"
                        action={<CreateStreamDialog onCreated={load} availablePlatforms={searchablePlatforms} />}
                    />
                ) : (
                    <div data-testid="streams-grid" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {streams.map((s) => (<StreamCard key={s.id} stream={s} onIngest={ingest} onRefresh={load} />))}
                    </div>
                )}
            </PageBody>
        </>
    );
}
